/**
 * Wishlist — localStorage-based wishlist with custom elements
 * No account required. Works for all visitors.
 */

/* ── Storage ── */
const WishlistStorage = {
  KEY: 'wishlist',

  _read() {
    try {
      return JSON.parse(localStorage.getItem(this.KEY)) || [];
    } catch {
      return [];
    }
  },

  _write(items) {
    localStorage.setItem(this.KEY, JSON.stringify(items));
  },

  getAll() {
    return this._read();
  },

  add(product) {
    const items = this._read();
    if (items.some((i) => i.handle === product.handle)) return;
    items.push(product);
    this._write(items);
  },

  remove(handle) {
    const items = this._read().filter((i) => i.handle !== handle);
    this._write(items);
  },

  has(handle) {
    return this._read().some((i) => i.handle === handle);
  },

  count() {
    return this._read().length;
  },
};

/* ── Header bubble update ── */
function updateWishlistBubble(action) {
  const count = WishlistStorage.count();
  document.querySelectorAll('.wishlist-count-bubble').forEach((bubble) => {
    const span = bubble.querySelector('span[aria-hidden]');
    if (span) span.textContent = count;
    bubble.style.display = count > 0 ? '' : 'none';
    // Scale-in animation when bubble first appears
    if (count > 0 && action === 'add') {
      bubble.classList.remove('wishlist-bubble--pop');
      void bubble.offsetWidth;
      bubble.classList.add('wishlist-bubble--pop');
    }
  });
  // Toggle filled heart state on header icon
  document.querySelectorAll('.header__icon--wishlist').forEach((icon) => {
    icon.classList.toggle('wishlist-has-items', count > 0);
    // Glow pulse on add
    if (action === 'add') {
      icon.classList.remove('wishlist-header--pulse');
      void icon.offsetWidth;
      icon.classList.add('wishlist-header--pulse');
    }
  });
}

/* ── Toast notification ── */
function showWishlistToast(title) {
  // Remove existing toast
  const existing = document.querySelector('.wishlist-toast');
  if (existing) existing.remove();

  const toast = document.createElement('div');
  toast.className = 'wishlist-toast';
  toast.innerHTML =
    '<svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>' +
    '<span>Added to wishlist</span>';
  document.body.appendChild(toast);

  // Trigger entrance
  requestAnimationFrame(() => toast.classList.add('wishlist-toast--visible'));

  // Auto-remove
  setTimeout(() => {
    toast.classList.remove('wishlist-toast--visible');
    toast.addEventListener('transitionend', () => toast.remove(), { once: true });
  }, 2200);
}

/* ── <wishlist-button> ── */
class WishlistButton extends HTMLElement {
  connectedCallback() {
    this.handle = this.dataset.handle;
    this.btn = this.querySelector('.wishlist-btn__toggle');
    if (!this.btn || !this.handle) return;
    this.updateState();
    this.btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.toggle();
    });
    document.addEventListener('wishlist:changed', () => this.updateState());
  }

  toggle() {
    const removing = WishlistStorage.has(this.handle);
    if (removing) {
      WishlistStorage.remove(this.handle);
    } else {
      WishlistStorage.add({
        handle: this.handle,
        title: this.dataset.title || '',
        url: this.dataset.url || '',
        price: this.dataset.price || '',
        comparePrice: this.dataset.comparePrice || '',
        image: this.dataset.image || '',
      });
    }
    const action = removing ? 'remove' : 'add';
    this.updateState();
    this.animate();
    updateWishlistBubble(action);
    if (action === 'add') showWishlistToast(this.dataset.title);
    document.dispatchEvent(new CustomEvent('wishlist:changed', {
      detail: { action, handle: this.handle },
    }));
  }

  updateState() {
    const active = WishlistStorage.has(this.handle);
    this.classList.toggle('wishlist-btn--active', active);
    if (this.btn) {
      this.btn.setAttribute(
        'aria-label',
        active ? 'Remove from wishlist' : 'Add to wishlist'
      );
    }
  }

  animate() {
    this.classList.add('wishlist-btn--pop');
    this.addEventListener('animationend', () => this.classList.remove('wishlist-btn--pop'), { once: true });
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

/* ── <wishlist-page> ── */
class WishlistPage extends HTMLElement {
  connectedCallback() {
    this.render();
    document.addEventListener('wishlist:changed', (e) => {
      const { action, handle } = e.detail || {};
      if (action === 'remove' && handle) {
        this.removeCard(handle);
      } else {
        this.render();
      }
    });
  }

  removeCard(handle) {
    const grid = this.querySelector('[data-wishlist-grid]');
    const empty = this.querySelector('[data-wishlist-empty]');
    const countEl = this.querySelector('[data-wishlist-count]');

    // Find the card's grid item by its wishlist-button handle
    const btn = grid?.querySelector(`wishlist-button[data-handle="${CSS.escape(handle)}"]`);
    const gridItem = btn?.closest('.grid__item');

    if (gridItem) {
      gridItem.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
      gridItem.style.opacity = '0';
      gridItem.style.transform = 'scale(0.95)';
      gridItem.addEventListener('transitionend', () => gridItem.remove(), { once: true });
    }

    // Update count
    const count = WishlistStorage.count();
    if (countEl) countEl.textContent = count;

    // Show empty state if no items left
    if (count === 0) {
      setTimeout(() => {
        if (grid) { grid.innerHTML = ''; grid.style.display = 'none'; }
        if (empty) empty.style.display = '';
      }, 350);
    }
  }

  async render() {
    const items = WishlistStorage.getAll();
    const grid = this.querySelector('[data-wishlist-grid]');
    const empty = this.querySelector('[data-wishlist-empty]');
    const countEl = this.querySelector('[data-wishlist-count]');
    const loader = this.querySelector('[data-wishlist-loader]');

    if (countEl) countEl.textContent = items.length;

    if (items.length === 0) {
      if (grid) { grid.innerHTML = ''; grid.style.display = 'none'; }
      if (empty) empty.style.display = '';
      if (loader) loader.style.display = 'none';
      return;
    }

    if (empty) empty.style.display = 'none';
    if (loader) loader.style.display = '';
    if (grid) grid.style.display = 'none';

    const cardHTMLs = await this.fetchCards(items);

    if (loader) loader.style.display = 'none';
    if (grid) {
      grid.style.display = '';
      grid.innerHTML = cardHTMLs.join('');
    }
  }

  async fetchCards(items) {
    const results = await Promise.all(items.map(async (item) => {
      try {
        const res = await fetch(`/products/${item.handle}?section_id=custom-wishlist-card`);
        if (res.ok) {
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const section = doc.querySelector('.shopify-section');
          if (section) return '<li class="grid__item">' + section.innerHTML + '</li>';
        }
      } catch { /* skip */ }
      return null;
    }));
    return results.filter(Boolean);
  }
}

if (!customElements.get('wishlist-page')) {
  customElements.define('wishlist-page', WishlistPage);
}

/* ── Init on load ── */
document.addEventListener('DOMContentLoaded', () => {
  updateWishlistBubble();
});
